import { Provider, ProviderType } from 'domains/models/account/providerType';
import * as amplitude from '@amplitude/analytics-browser';
import { UserItem } from 'presentations/api/schema/user';
import { PlanId } from 'domains/models/subscription/planId';
import { ListViewType } from 'domains/models/form/listViewType';
import { VisualViewType } from 'domains/models/form/visualViewType';
import { ProductType } from 'domains/models/blog_post/productType';
import { LocationType } from 'domains/models/event_type/locationType';

export class Amplitude {
  static signUpLeadClick = (props: {
    pageType: 'magazine' | 'top' | 'calendar' | 'eventType' | 'event';
    to: 'signUp' | 'top';
    position: 'header' | 'footer' | 'inline';
    provider?: Provider;
  }) => {
    this.track('click sign up', props);
  };

  static eventTypePageView = (props: {
    uid: string;
    eventTypePlanId: PlanId;
    isLoginViewer: boolean;
    loadingTime: number;
  }) => {
    this.track('event type page view', props);
  };

  static salesContactClick = (props: {
    pageType: 'magazine' | 'top' | 'calendar';
    position?: 'inline';
  }) => {
    this.track('click sales contact', props);
  };

  static customerStoryPageView = (props: { slug: string }) => {
    this.track('customer story page view', props);
  };

  static magazinePageView = (props: {
    categories: string[];
    tags: string[];
    slug: string;
    title: string;
  }) => {
    this.track('magazine page view', props);
  };

  static magazineAdClick = (props: {
    position: 'inline' | 'display' | 'footer' | 'modal';
    contentType: 'top' | 'story';
    contentId?: string;
    productType?: ProductType;
  }) => {
    this.track('click resource banner', props);
  };

  static calendarLpAdClick = (props: {
    position: 'display' | 'footer';
    contentId?: string;
    productType?: ProductType;
  }) => {
    this.track('click calendar banner', props);
  };

  static magazineScrollModal = (props: {
    categories: string[];
    tags: string[];
    slug: string;
    title: string;
    contentId: string;
  }) => {
    this.track('magazine scroll modal show', props);
  };

  static apiResponseError = (props: {
    status: number;
    requestUrl: string;
    method: string;
    errorMessage: string;
    errors: any;
    data: any;
  }) => {
    this.track('api error', props);
  };

  static formPageView = (props: {
    uid: string;
    formPlanId: PlanId;
    isLoginViewer: boolean;
    listViewType: ListViewType;
    visualViewType: VisualViewType;
  }) => {
    this.track('form page view', props);
  };

  static calendarConnectButtonClick = (props: {
    provider: Provider;
    position: 'calendar';
    content?: string;
  }) => {
    this.track('sign up on booking calendar', props);
  };

  static login = (user: UserItem) => {
    if (!this.configure()) return;
    this.setUserId(user.uid);
    this.track('login');
  };

  static accountOnboarding = async (props: {
    step: number;
    productType?: ProductType;
  }) => {
    try {
      this.track('onboard account', props);
    } catch (error) {
      console.debug(error);
    }
  };

  static accountOnboardingCompleted = async (props: {
    productType: ProductType;
  }) => {
    try {
      this.track('onboard account completed', props);
    } catch (error) {
      console.debug(error);
    }
  };

  static eventCreated = async (props: {
    locationId: LocationType | null;
    isWidget: boolean;
    eventTypePlanId: PlanId;
    isHostTeam: boolean;
  }) => {
    try {
      this.track('event create', props);
    } catch (error) {
      console.debug(error);
    }
  };

  static teamCreated = async (props: { teamId: string; isSignUp: boolean }) => {
    try {
      this.track('team create', props);
    } catch (error) {
      console.debug(error);
    }
  };

  static adminInviteUser = async (props: { planId: PlanId }) => {
    try {
      this.track('admin invite user', props);
    } catch (error) {
      console.debug(error);
    }
  };

  static helpPageView = async (props: { url: string; title?: string }) => {
    try {
      this.track('help page view', props);
    } catch (error) {
      console.debug(error);
    }
  };

  static signUp = (
    user: UserItem,
    props: {
      providerType: ProviderType;
      source: string | null;
      medium: string | null;
      campaign: string | null;
      term: string | null;
      content: string | null;
      referrer: string | null;
      landingUrl: string;
    },
  ) => {
    if (typeof window === 'undefined') return;
    if (!this.configure()) return;
    if (!user || !user.email) return;
    try {
      const identify = new amplitude.Identify()
        .set('email', user.email)
        .set('name', user.name)
        .set('providerType', props.providerType)
        .set('source', props.source || '')
        .set('medium', props.medium || '')
        .set('campaign', props.campaign || '')
        .set('term', props.term || '')
        .set('content', props.content || '')
        .set('referrer', props.referrer || '')
        .set('url', props.landingUrl || '')
        .set('planId', PlanId.Free);
      amplitude.identify(identify);
      this.track('sign up', props);
    } catch (error) {
      console.debug(error);
    }
  };

  static setGroup = async (groupType: string, uid: string) => {
    if (typeof window === 'undefined') return;
    if (!this.configure()) return;
    try {
      amplitude.setGroup(groupType, uid);
    } catch (error) {
      console.debug(error);
    }
  };

  static pageview = (props?: any) => {
    try {
      amplitude.track('Page Viewed', {
        url: window.location.href,
        ...props,
      });
    } catch (error) {
      console.debug(error);
    }
  };

  static setUserId = async (userId: string) => {
    if (typeof window === 'undefined') return;
    if (!this.configure()) return;
    try {
      amplitude.setUserId(userId);
    } catch (error) {
      console.debug(error);
    }
  };

  static setPlanId = async (planId: PlanId) => {
    if (typeof window === 'undefined') return;
    if (!this.configure()) return;
    try {
      const identify = new amplitude.Identify().set('planId', planId);
      amplitude.identify(identify);
    } catch (error) {
      console.debug(error);
    }
  };

  // private

  private static configure = () => {
    const id = process.env.AMPLITUDE_TRACKING_ID;
    if (!id) return false;
    amplitude.init(id, {
      defaultTracking: false,
    });
    return true;
  };

  private static track = (action: string, props?: any) => {
    if (typeof window === 'undefined') return;
    if (!this.configure()) return;
    try {
      amplitude.logEvent(action, {
        url: window.location.href,
        ...props,
      });
    } catch (error) {
      console.debug(error);
    }
  };
}
